import {
  useEffect, forwardRef, useImperativeHandle, useState, useCallback,
} from "react";
import { useForm, Controller } from "react-hook-form";
import Select       from "react-select";
import DatePicker   from "react-datepicker";
import toast        from "react-hot-toast";
import "./IntakeForm.css";
import SignaturePad  from "../../../Component/shared/signaturePad";
import SpinnerLoader from "../../../Component/shared/loader/spinnerLoader/SpinnerLoader";
import {
  BuildInsuranceAddress, getCaseTypeNameById, getMaritalStatus,
  getPatientAge, getSessionData, getStateNameById,
} from "../../../helpers/Util";
import { IntakeSheetSession, IntakeSheetType, PatientDetails } from "../../../Constant";
import { GetIntakeFormData, saveIntakeForm } from "./services";
import { removeIntakeSheets } from "../assignmentOfBenefit/Service";
import { usePatientData } from "../../../commonFunction/storage/Patientdatacontext";

const IS_PREGNANT_OPTIONS = [
  { label: "Yes", value: 1 },
  { label: "No",  value: 0 },
];
const RELATION_OPTIONS = [
  { label: "Self",         value: "1" },
  { label: "Spouse",       value: "2" },
  { label: "Child",        value: "3" },
  { label: "Other",        value: "4" },
  { label: "Individual",   value: "5" },
  { label: "Organization", value: "6" },
  { label: "Employee",     value: "7" },
];
const YES_NO_OPTIONS = [
  { label: "Yes", value: 1 },
  { label: "No",  value: 0 },
];
const DEFAULT_VALUES = {
  IsPregnant: null, IsWorkingCurrently: null, DateMissedWork: null,
  DateOfSymptom: null, InsurdsDOB: null, InsurdsRelation: null,
  EmergencyContactNameForIntake: "", EmergencyContactForIntake: "",
  RelationshipForIntake: "", DetailOfAccident: "", InjuriesCaseEstablished: "",
  IsReferral: 0, IsAdvertise: 0, IsOthers: 0, explainTType: "",
};

const getIsWorkingCurently = (data) => {
  if (!data) return "";
  if (data.IsWorkingCurrently === true)  return 1;
  if (data.IsWorkingCurrently === false) return 0;
  return "";
};


const IntakeForm = forwardRef(function IntakeForm(props, ref) {
  const { companyId, copySignature, signatureMiss, BaseUrl, onReady } = props;

  const SessionData = getSessionData();
  const tokenData   = getSessionData("tokenData", {});
  const user        = getSessionData("User", {});
  const formName    = IntakeSheetType.IntakeForm;

  const { control, reset, watch, getValues } = useForm({ defaultValues: DEFAULT_VALUES });

  const watchIsReferral  = watch("IsReferral");
  const watchIsAdvertise = watch("IsAdvertise");
  const watchIsOthers    = watch("IsOthers");
  const showExplain      = watchIsReferral === 1 || watchIsAdvertise === 1 || watchIsOthers === 1;

  const [loading,         setLoading]         = useState(false);
  const [caseFormDataId,  setCaseFormDataId]  = useState("");
  const [dataInfo,        setDataInfo]        = useState({});
  const [patientSignImg,  setPatientSignImg]  = useState("");
  const [providerSignImg, setProviderSignImg] = useState("");
  const [sigPadOpen,      setSigPadOpen]      = useState(false);
  const [signRole,        setSignRole]        = useState("");

  const { patientData, fetchPatientData } = usePatientData();

  const callIntakeFormGetApi = useCallback(async () => {
    setLoading(true);
    try {

      const patientInfo = patientData || await fetchPatientData(tokenData?.caseId, tokenData?.companyId);
      const res      = await GetIntakeFormData(companyId, tokenData.caseId, tokenData.patientId);
      const response = await res.json();

      if (!response?.Record) { signatureMiss?.(true); return; }

      const data = response.Record;
      setDataInfo(data);
      setCaseFormDataId(data?.CaseFormDataId || "");

      const doa = data.DOA ? new Date(data.DOA) : null;

      reset({
        IsPregnant:
          data.IsPregnant != null
            ? IS_PREGNANT_OPTIONS.find(o => o.value === data.IsPregnant) || null
            : null,
        IsWorkingCurrently:          getIsWorkingCurently(data),
        DateMissedWork:              data.DateMissedWork ? new Date(data.DateMissedWork) : null,
        DateOfSymptom:               data.DateOfSymptom  ? new Date(data.DateOfSymptom)  : null,
        InsurdsDOB:                  data.InsurdsDOB     ? new Date(data.InsurdsDOB)     : null,
        InsurdsRelation:             patientInfo?.policyHolderRelation || "",
        EmergencyContactNameForIntake: data.EmergencyContactNameForIntake || "",
        EmergencyContactForIntake:   data.EmergencyContactForIntake   || "",
        RelationshipForIntake:       data.RelationshipForIntake        || "",
        DetailOfAccident:            data.DetailOfAccident             || "",
        InjuriesCaseEstablished:     data.InjuriesCaseEstablished      || "",
        IsReferral:                  data.IsReferral  ?? 0,
        RefferedByWhom:              data.RefferedByWhom || "",
        IsAdvertise:                 data.IsAdvertise ?? 0,
        WhichAdv:                    data.WhichAdv    || "",
        IsOthers:                    data.IsOthers    ?? 0,
        Others:                      data.Others      || "",
        explainTType:                data.RefferedByWhom || data.WhichAdv || data.Others || "",
        DOA:                         doa && doa.getFullYear() > 1900 ? doa : null,
      });

      if (data.ProviderSignature) {
        setProviderSignImg(`data:image/png;base64,${data.ProviderSignature}`);
      }
      if (data.PatientSignature) {
        const sig = `data:image/png;base64,${data.PatientSignature}`;
        setPatientSignImg(sig);
        sessionStorage.setItem("sharedPatientSignature", sig);
      } else {
        signatureMiss?.(true);
      }
    } catch (err) {
      console.error("[IntakeForm] callIntakeFormGetApi error:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId, patientData, fetchPatientData, tokenData, user, reset, signatureMiss]);

  useEffect(() => {
    if (tokenData.caseId && tokenData.patientId && companyId) {
      callIntakeFormGetApi();
    }
  }, [tokenData.caseId, tokenData.patientId, companyId]);

  useEffect(() => { onReady?.("intake_form"); }, [patientSignImg]);

  useImperativeHandle(ref, () => ({
    save:           ()    => saveFunction(),
    isPending:      ()    => !patientSignImg,
    getSignature:   ()    => patientSignImg || "",
    applySignature: (sig) => { if (sig) setPatientSignImg(sig); },

    load:           ()    => callIntakeFormGetApi(),
  }), [patientSignImg, callIntakeFormGetApi]);


  const buildRequestObj = (formValues, isCopySign = false) => {
    const patientSig  = patientSignImg?.split("data:image/png;base64,")[1]  || "";
    const providerSig = providerSignImg?.split("data:image/png;base64,")[1] || "";
    const pd = patientData || {};

    return {
      CaseId:      tokenData.caseId,
      PatientId:   tokenData.patientId,
      CompanyId:   companyId,
      CaseFormDataId: caseFormDataId,
      FormName:    formName,
      UserId:      tokenData?.userId,
      IsCopySign:  isCopySign,

      MaritalStatus:  dataInfo?.MaritalStatus,
      CaseType:       getCaseTypeNameById(pd?.caseTypeId) || "",
      IsPregnant:
        formValues.IsPregnant != null
          ? formValues.IsPregnant?.value ?? formValues.IsPregnant
          : dataInfo?.IsPregnant,
      IsOccupation:       formValues.IsWorkingCurrently ?? dataInfo?.IsOccupation ?? null,
      IsWorkingCurrently: formValues.IsWorkingCurrently ?? null,

      InsurdName:       formValues.InsurdName      || dataInfo?.InsurdName,
      InsuranceName:    formValues.InsuranceName   || dataInfo?.InsuranceName,
      InsuranceAddress: formValues.InsuranceAddress || dataInfo?.InsuranceAddress,
      InsurdsRelation:  formValues.InsurdsRelation?.value ?? dataInfo?.InsurdsRelation,
      InsurdsDOB:       formValues.InsurdsDOB,
      MemberNo: "", GroupNo: "",

      EmergencyContactNameForIntake: formValues.EmergencyContactNameForIntake || dataInfo?.EmergencyContactNameForIntake,
      EmergencyContactForIntake:     formValues.EmergencyContactForIntake     || dataInfo?.EmergencyContactForIntake,
      RelationshipForIntake:         formValues.RelationshipForIntake          || dataInfo?.RelationshipForIntake,
      DateOfSymptom:   formValues.DateOfSymptom  || dataInfo?.DateOfSymptom,
      DateMissedWork:  formValues.DateMissedWork || dataInfo?.DateMissedWork,
      DetailOfAccident:      formValues.DetailOfAccident      || dataInfo?.DetailOfAccident,
      InjuriesCaseEstablished: formValues.InjuriesCaseEstablished || dataInfo?.InjuriesCaseEstablished,

      IsReferral:    formValues.IsReferral,
      RefferedByWhom: formValues.IsReferral === 1 ? formValues.explainTType : dataInfo?.RefferedByWhom,
      IsAdvertise:   formValues.IsAdvertise,
      WhichAdv:      formValues.IsAdvertise === 1 ? formValues.explainTType : dataInfo?.WhichAdv,
      IsOthers:      formValues.IsOthers,
      Others:        formValues.IsOthers === 1    ? formValues.explainTType : dataInfo?.Others,

      Provider:    tokenData.providerName || "",
      ProviderId:  tokenData.providerId   || "",

      formData: [
        { FormName: "IntakePatient",  Signature: patientSig  },
        { FormName: "IntakeProvider", Signature: providerSig },
      ],
    };
  };

  const saveFunction = async () => {
    const formValues = getValues();
    setLoading(true);
    try {
      const requestObj = buildRequestObj(formValues, copySignature);
      const response   = await saveIntakeForm(requestObj);
      if (response?.HasException) {
        toast.error(`Error saving Intake Form. ${response?.MessageText || ""}`);
        return false;
      }
      toast.success("Intake Form saved successfully!");
      removeIntakeSheets();
      return true;
    } catch (err) {
      toast.error(err?.MessageText || "Failed to save Intake Form.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const signUpdatedCall = (base64Img) => {
    const normalized = base64Img.includes(IntakeSheetSession.dataBlock1)
      ? base64Img.replace(IntakeSheetSession.dataBlock1, IntakeSheetSession.dataBlock)
      : base64Img;
    if (signRole === "Provider") {
      setProviderSignImg(normalized);
    } else {
      setPatientSignImg(normalized);
      sessionStorage.setItem("sharedPatientSignature", normalized);
    }
  };
  const clearSignCall = () => {
    if (signRole === "Patient") { setPatientSignImg(""); signatureMiss?.(true); }
    else                         setProviderSignImg("");
  };

  const pd = patientData || {};

  const ProviderField = ({ inline = false }) => (
    <input className="if__input" type="text" value={tokenData.providerName || ""} disabled
      style={inline ? { width: "200px", display: "inline-block" } : {}} />
  );

  if (loading) return <SpinnerLoader message="Loading form data…" />;

  return (
    <div className="if__wrapper">
      <div className="if__title-block">
        <div className="if__title-main">Intake Form</div>
      </div>

      <div className="if__section">
        <span className="if__section-title">Patient Information</span>
        <span className="if__section-line" />
      </div>

      <div className="if__grid if__grid--5">
        <div className="if__field">
          <label className="if__label">Current Date</label>
          <input className="if__input" type="text" value={new Date().toLocaleDateString()} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">DOA</label>
          <DatePicker dateFormat="MM/dd/yyyy"
            selected={pd?.patientDOA === "1900-01-01T00:00:00" ? null : pd?.patientDOA}
            onChange={() => {}} disabled className="if__input" />
        </div>
        <div className="if__field">
          <label className="if__label">Patient Name</label>
          <input className="if__input" type="text"
            value={`${pd?.patientFirstName || ""} ${pd?.patientLastName || ""}`.trim()} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Social Security #</label>
          <input className="if__input" type="text" value={pd?.ssn || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Mailing Address</label>
          <input className="if__input" type="text" value={pd?.patientAddress || ""} disabled />
        </div>
      </div>

      <div className="if__grid if__grid--5">
        <div className="if__field">
          <label className="if__label">City</label>
          <input className="if__input" type="text" value={pd?.patientCity || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">State</label>
          <input className="if__input" type="text" value={getStateNameById(pd?.patientStateId)} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Zip</label>
          <input className="if__input" type="text" value={pd?.patientZIP || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Age</label>
          <input className="if__input" type="text" value={getPatientAge(pd?.patientDOB) || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Sex</label>
          <input className="if__input" type="text" value={pd?.patientGender || ""} disabled />
        </div>
      </div>

      <div className="if__grid if__grid--5">
        <div className="if__field">
          <label className="if__label">Marital Status</label>
          <input className="if__input" type="text" value={getMaritalStatus(pd?.patientMaritalStatus) || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Phone</label>
          <input className="if__input" type="text" value={pd?.patientCellNumber || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Female only (are you pregnant?)</label>
          <Controller name="IsPregnant" control={control}
            render={({ field }) => (
              <Select classNamePrefix="if-select" placeholder="Are you pregnant?" options={IS_PREGNANT_OPTIONS}
                value={IS_PREGNANT_OPTIONS.find(c => c.value === (field.value?.value ?? field.value)) || null}
                onChange={val => field.onChange(val)} />
            )} />
        </div>
        <div className="if__field">
          <label className="if__label">Case Type</label>
          <input className="if__input" type="text" value={getCaseTypeNameById(pd?.caseTypeId) || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Insurance Name</label>
          <input className="if__input" type="text" value={pd?.insuranceName || ""} disabled />
        </div>
      </div>

      <div className="if__section">
        <span className="if__section-title">Insurance &amp; Emergency Contact</span>
        <span className="if__section-line" />
      </div>

      <div className="if__grid if__grid--5">
        <div className="if__field">
          <label className="if__label">Insured Name</label>
          <input className="if__input" type="text"
            value={`${pd?.policyHolderFirstName || ""} ${pd?.policyHolderLastName || ""}`.trim()} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Insurance Company Address</label>
          <input className="if__input" type="text" value={BuildInsuranceAddress(pd) || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Adjuster Phone</label>
          <input className="if__input" type="text" value={pd?.adjusterPhone || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Emergency Contact Name</label>
          <Controller name="EmergencyContactNameForIntake" control={control}
            render={({ field }) => <input className="if__input" type="text" {...field} />} />
        </div>
        <div className="if__field">
          <label className="if__label">Emergency Phone</label>
          <Controller name="EmergencyContactForIntake" control={control}
            render={({ field }) => <input className="if__input" type="text" {...field} />} />
        </div>
      </div>

      <div className="if__grid if__grid--5">
        <div className="if__field">
          <label className="if__label">Relationship</label>
          <Controller name="RelationshipForIntake" control={control}
            render={({ field }) => <input className="if__input" type="text" {...field} />} />
        </div>
        <div className="if__field">
          <label className="if__label">Currently working?</label>
          <Controller name="IsWorkingCurrently" control={control}
            render={({ field }) => (
              <Select classNamePrefix="if-select" options={YES_NO_OPTIONS}
                value={YES_NO_OPTIONS.find(c => c.value === field.value) ?? null}
                onChange={val => field.onChange(val?.value ?? null)} />
            )} />
        </div>
        <div className="if__field">
          <label className="if__label">Date you first missed work</label>
          <Controller name="DateMissedWork" control={control}
            render={({ field }) => (
              <DatePicker dateFormat="MM/dd/yyyy" selected={field.value} onChange={d => field.onChange(d)} className="if__input" />
            )} />
        </div>
        <div className="if__field">
          <label className="if__label">Date of first symptom</label>
          <Controller name="DateOfSymptom" control={control}
            render={({ field }) => (
              <DatePicker dateFormat="MM/dd/yyyy" selected={field.value} onChange={d => field.onChange(d)} className="if__input" />
            )} />
        </div>
        <div className="if__field">
          <label className="if__label">How did the accident happen?</label>
          <Controller name="DetailOfAccident" control={control}
            render={({ field }) => <input className="if__input" type="text" {...field} />} />
        </div>
      </div>

      <div className="if__section">
        <span className="if__section-title">Workers Comp Only</span>
        <span className="if__section-line" />
      </div>

      <div className="if__grid if__grid--4">
        <div className="if__field">
          <label className="if__label">Claim #</label>
          <input className="if__input" type="text" value={pd?.claimNumber || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Policy # (Carrier Id #)</label>
          <input className="if__input" type="text" value={pd?.policyNumber || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">WCB #</label>
          <input className="if__input" type="text" value={pd?.wcbNo || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Employer Name</label>
          <input className="if__input" type="text" value={pd?.employerName || ""} disabled />
        </div>
      </div>

      <div className="if__grid if__grid--4">
        <div className="if__field">
          <label className="if__label">Attorney Name</label>
          <input className="if__input" type="text"
            value={`${pd?.attorneyFName || ""} ${pd?.attorneyLName || ""}`.trim()} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Attorney Phone</label>
          <input className="if__input" type="text" value={pd?.attorneyPhone || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Injuries Case Established for</label>
          <Controller name="InjuriesCaseEstablished" control={control}
            render={({ field }) => <input className="if__input" type="text" {...field} />} />
        </div>
        <div className="if__field">
          <label className="if__label">How did you hear about us?</label>
          <div className="if__checkbox-group">
            {[{ key: "IsReferral", label: "Referral" }, { key: "IsAdvertise", label: "Advertise" }, { key: "IsOthers", label: "Others" }]
              .map(({ key, label }) => (
                <Controller key={key} name={key} control={control}
                  render={({ field }) => (
                    <label className="if__checkbox-label">
                      <input type="checkbox" className="if__checkbox"
                        checked={field.value === 1}
                        onChange={e => field.onChange(e.target.checked ? 1 : 0)} />
                      {label}
                    </label>
                  )} />
              ))}
          </div>
          {showExplain && (
            <Controller name="explainTType" control={control}
              render={({ field }) => (
                <input className="if__input" style={{ marginTop: 6 }} type="text" placeholder="Please specify…" {...field} />
              )} />
          )}
        </div>
      </div>

      <div className="if__section">
        <span className="if__section-title">No-Fault Authorization</span>
        <span className="if__section-line" />
      </div>
      <div className="if__legal-panel">
        <p><b>NO-FAULT AUTHORIZATION</b></p>
        <p>PATIENT: Your health provider may agree to accept payment for health services performed directly from your insurer (Authorization to Pay Benefits) so that you are not required to make payment to the health provider at the time of service. Such agreement is optional on the part of the health provider and must be signed by both patient and health provider. You may use the optional authorization language provided below, by checking off the designated spot on this form.</p>
        <p>(INITIAL HERE IF YOU HAVE CHOSEN TO AUTHORIZE THE DIRECT PAYMENT OF BENEFITS)</p>
        <p style={{ marginTop: 8 }}><b>AUTHORIZATION TO PAY BENEFITS:</b> I AUTHORIZE PAYMENT OF HEALTH BENEFITS TO THE UNDERSIGNED HEALTHCARE PROVIDER OR SUPPLIER OF SERVICES DESCRIBED BELOW. I RETAIN ALL RIGHTS, PRIVILEGES, AND REMEDIES TO WHICH I AM ENTITLED UNDER ARTICLE 51 (THE NO-FAULT PROVISION) OF THE INSURANCE LAW.</p>
      </div>

      <div className="if__section">
        <span className="if__section-title">Workers Compensation Assignment of Benefits</span>
        <span className="if__section-line" />
      </div>
      <div className="if__wc-sentence">
        <p>
          I understand that if my Workers Compensation insurance denies payment for services that have been rendered to me I will be financially liable for those services. In the event that my Workers Compensation insurance denies paying my claim, I authorize{" "}
          <span className="if__wc-provider"><ProviderField inline /></span>{" "}
          to bill my major medical carrier for those services. I agree that if my major medical carrier refuses to pay for those services, I will continue to be financially liable for the unpaid service.
        </p>
      </div>

      <div className="if__section">
        <span className="if__section-title">Major Medical Insurance</span>
        <span className="if__section-line" />
      </div>
      <div className="if__grid if__grid--4">
        <div className="if__field">
          <label className="if__label">Insurance Company Name</label>
          <input className="if__input" type="text" value={pd?.insuranceName || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Insurance Company Address</label>
          <input className="if__input" type="text" value={pd?.insuranceAddress || ""} disabled />
        </div>
        <div className="if__field">
          <label className="if__label">Insured Relationship</label>
          <Select classNamePrefix="if-select" options={RELATION_OPTIONS}
            value={RELATION_OPTIONS.find(c => c.value === pd?.policyHolderRelation) || null}
            isDisabled />
        </div>
        <div className="if__field">
          <label className="if__label">Insured Date of Birth</label>
          <Controller name="InsurdsDOB" control={control}
            render={({ field }) => (
              <DatePicker dateFormat="MM/dd/yyyy" selected={field.value} onChange={d => field.onChange(d)} disabled className="if__input" />
            )} />
        </div>
      </div>

      <div className="if__section">
        <span className="if__section-title">Signatures</span>
        <span className="if__section-line" />
      </div>
      <div className="if__sig-row">
        <div className="if__sig-col">
          <span className="if__sig-label"><b>Your Signature</b></span>
          <div className={`if__sign-box${!patientSignImg ? " if__sign-box--empty" : ""}`}
            onClick={() => { setSignRole("Patient"); setSigPadOpen(true); }}>
            {patientSignImg && <img src={patientSignImg} alt="Patient signature" className="if__sign-img" />}
          </div>
        </div>
        <div className="if__sig-col">
          <span className="if__sig-label"><b>Provider Signature</b></span>
          <div className="if__sign-box if__sign-box--disabled" disabled>
            {providerSignImg && <img src={providerSignImg} alt="Provider signature" className="if__sign-img" />}
          </div>
        </div>
      </div>
      <p className="if__footer-note">All of the above information, to the best of my knowledge, has been filled out correctly.</p>

      <SignaturePad
        isOpen={sigPadOpen}
        title={signRole === "Patient" ? "Patient Signature" : "Provider Signature"}
        existingSign={signRole === "Patient" ? patientSignImg : providerSignImg}
        onClose={() => setSigPadOpen(false)}
        onSave={(base64) => { signUpdatedCall(base64); setSigPadOpen(false); }}
        onClear={clearSignCall}
      />
    </div>
  );
});

export default IntakeForm;